import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { API_BASE, getImageUrl } from '../utils/api'
import { blogCategories, tagCloud } from '../data/data'

interface Blog {
    _id: string;
    title: string;
    date: string;
    image: string;
}

export default function BlogSidebar() {
    const [trending, setTrending] = useState<Blog[]>([])
    const [categories, setCategories] = useState<{title: string, count: number}[]>([])
    const [tags, setTags] = useState<string[]>([])
    const [search, setSearch] = useState('')

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await fetch(`${API_BASE}/blogs`)
                const data = await res.json()
                if (Array.isArray(data)) {
                    // Trending (top 3 by views)
                    const sorted = [...data].sort((a, b) => (b.views || 0) - (a.views || 0))
                    setTrending(sorted.slice(0, 3))

                    // Categories
                    const catMap: {[key: string]: number} = {}
                    data.forEach(blog => {
                        const cat = blog.category || 'Real Estate'
                        catMap[cat] = (catMap[cat] || 0) + 1
                    })
                    setCategories(Object.entries(catMap).map(([title, count]) => ({ title, count })))

                    // Tags Cloud
                    const allTags = new Set<string>()
                    data.forEach(blog => {
                        if (blog.tags) blog.tags.forEach((t: string) => allTags.add(t))
                    })
                    setTags(Array.from(allTags).slice(0, 10))
                }
            } catch (err) {
                console.error(err)
            }
        }
        fetchData()
    }, [])

    return (
        <div className="space-y-8">
            {/* Search Widget */}
            <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm transition-all hover:shadow-xl group">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-[#001b4a] mb-6 flex items-center gap-3">
                    <span className="w-1.5 h-6 bg-[#f1ae24] rounded-full group-hover:h-8 transition-all"></span>
                    Search Universe
                </h4>
                <div className="relative">
                    <i className="fa-solid fa-magnifying-glass absolute left-5 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#f1ae24] transition-colors"></i>
                    <input 
                        type="search" 
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Discover insights..." 
                        className="w-full pl-14 pr-6 py-4 bg-slate-50 border-none rounded-2xl text-xs font-bold text-slate-700 placeholder:text-slate-300 focus:ring-2 focus:ring-[#f1ae24]/20 transition-all shadow-inner"
                    />
                </div>
            </div>

            {/* Categories Widget */}
            <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm transition-all hover:shadow-xl group">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-[#001b4a] mb-6 flex items-center gap-3">
                    <span className="w-1.5 h-6 bg-[#f1ae24] rounded-full group-hover:h-8 transition-all"></span>
                    Intelligence Hub
                </h4>
                <ul className="space-y-4">
                    {categories.map((item, index) => (
                        <li key={index}>
                            <Link to="#" className="flex items-center justify-between group/item p-1">
                                <span className="text-slate-500 font-bold text-xs uppercase tracking-widest group-hover/item:text-[#001b4a] transition-all flex items-center gap-3">
                                    <i className="fa-solid fa-folder-open text-[#f1ae24] opacity-50 text-[10px]"></i>
                                    {item.title}
                                </span>
                                <span className="w-6 h-6 rounded-lg bg-slate-50 flex items-center justify-center text-[10px] font-black text-slate-400 group-hover/item:bg-[#001b4a] group-hover/item:text-white transition-all shadow-sm">
                                    {item.count}
                                </span>
                            </Link>
                        </li>
                    ))}
                    {categories.length === 0 && <li className="text-slate-300 text-[10px] font-bold uppercase italic px-4">Populating hubs...</li>}
                </ul>
            </div>

            {/* Trending Posts Widget */}
            <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm transition-all hover:shadow-xl group">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-[#001b4a] mb-8 flex items-center gap-3">
                    <span className="w-1.5 h-6 bg-[#f1ae24] rounded-full group-hover:h-8 transition-all"></span>
                    Popular Signals
                </h4>
                <div className="space-y-8">
                    {trending.map((item: Blog, index: number) => (
                        <div key={index} className="flex gap-4 group/item cursor-pointer">
                            <div className="w-20 h-20 rounded-2xl overflow-hidden shrink-0 shadow-md group-hover/item:rotate-2 transition-transform duration-500">
                                <img src={getImageUrl(item.image)} alt="" className="w-full h-full object-cover group-hover/item:scale-110 transition-transform duration-700" />
                            </div>
                            <div className="flex flex-col justify-center">
                                <Link to={`/blog-detail/${item._id}`} className="text-xs font-black text-black group-hover/item:text-[#001b4a] transition-colors line-clamp-2 leading-snug mb-2 uppercase tracking-wide">
                                    {item.title}
                                </Link>
                                <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest flex items-center gap-2">
                                    <i className="fa-regular fa-calendar-days text-[#f1ae24]"></i>
                                    {item.date}
                                </span>
                            </div>
                        </div>
                    ))}
                    {trending.length === 0 && <div className="text-slate-300 text-[10px] font-bold uppercase italic px-4">Detecting signals...</div>}
                </div>
            </div>

            {/* Tags Widget */}
            <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm transition-all hover:shadow-xl group">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-[#001b4a] mb-6 flex items-center gap-3">
                    <span className="w-1.5 h-6 bg-[#f1ae24] rounded-full group-hover:h-8 transition-all"></span>
                    Tag Cloud
                </h4>
                <div className="flex flex-wrap gap-2">
                    {tags.map((item, index) => (
                        <Link key={index} to="#" className="px-4 py-2 bg-slate-50 text-slate-500 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[#001b4a] hover:text-white transition-all shadow-sm active:scale-95">
                            #{item}
                        </Link>
                    ))}
                    {tags.length === 0 && <div className="text-slate-300 text-[10px] font-bold uppercase italic px-4 w-full">Atmosphere clear...</div>}
                </div>
            </div>
        </div>
    )
}
